import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { useReferralsTracking } from '~/components/Referral/components/ReferralsPage/hooks/useReferralsTracking';
import { useMount } from 'react-use';
import { useState, useCallback } from 'react';
import { campaignDetailsStore } from '~/components/Referral/components/ReferralsPage/stores/campaign-details.store';
import { sleep } from '~/utils/data.utils';

// to avoid multiple requests for the same campaign details until the request is completed
let isFetchingCampaignDetails = false;

const MAX_CAMPAIGN_FETCH_RETRIES = 4;
const CAMPAIGN_FETCH_RETRY_DELAY_MS = 1000; // 1 second

export const useCampaignDetails = () => {
    const {
        userStore: { currUser },
        userService
    } = useGlobalMX();

    const [isCampaignLoading, setIsCampaignLoading] = useState(true);

    const { trackReferralsError, trackReferralsSuccess } =
        useReferralsTracking();

    const fetchCampaignDetails = useCallback(
        async (forceRefetch = false) => {
            try {
                if (
                    !forceRefetch &&
                    (campaignDetailsStore.campaignDetails.campaign_uid ||
                        isFetchingCampaignDetails)
                ) {
                    return;
                }
                isFetchingCampaignDetails = true;
                setIsCampaignLoading(true);

                let campaignDetails;

                // Campaign creation is asynchronous on the backend and may take some time to complete
                // after subscription, so we retry fetching campaign details for refetch calls
                for (
                    let attempt = 0;
                    attempt <= MAX_CAMPAIGN_FETCH_RETRIES;
                    attempt++
                ) {
                    campaignDetails =
                        await userService.getReferralCampaignDetails(
                            currUser._id
                        );

                    if (campaignDetails?.campaign_uid || !forceRefetch) {
                        break;
                    }

                    // Only wait if we're going to retry (not on the last attempt)
                    if (attempt < MAX_CAMPAIGN_FETCH_RETRIES) {
                        await sleep(CAMPAIGN_FETCH_RETRY_DELAY_MS);
                    }
                }

                campaignDetailsStore.setCampaignDetails(campaignDetails);
                trackReferralsSuccess({
                    message: 'get referral campaign',
                    context: { campaign: campaignDetails._id }
                });
            } catch (error) {
                trackReferralsError({
                    error,
                    message: 'get referral campaign'
                });
            } finally {
                setIsCampaignLoading(false);
                isFetchingCampaignDetails = false;
            }
        },
        [currUser._id, trackReferralsError, trackReferralsSuccess, userService]
    );

    useMount(fetchCampaignDetails);

    const refetchCampaignDetails = useCallback(() => {
        fetchCampaignDetails(true);
    }, [fetchCampaignDetails]);

    return {
        campaignDetails: campaignDetailsStore.campaignDetails,
        isCampaignLoading,
        isCampaignFetched:
            Object.keys(campaignDetailsStore.campaignDetails).length !== 0,
        refetchCampaignDetails
    };
};
